import React, { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { UploadCloud, Download, Trash2, File as FileIcon, Loader2, HardDrive, Lock } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";

function humanSize(bytes) {
  if (!bytes) return "0 B";
  const u = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(i ? 1 : 0)} ${u[i]}`;
}

export default function BlobManager() {
  const qc = useQueryClient();
  const inputRef = useRef();
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const { data } = useQuery({
    queryKey: ["blobs"],
    queryFn: async () => (await api.get("/blobs")).data,
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["blobs"] });
    qc.invalidateQueries({ queryKey: ["kpis"] });
    qc.invalidateQueries({ queryKey: ["services"] });
  };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/blobs", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Uploaded ${file.name}`);
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const download = async (b) => {
    try {
      const res = await api.get(`/blobs/${b.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = b.filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Download failed");
    }
  };

  const remove = async (b) => {
    try {
      await api.delete(`/blobs/${b.id}`);
      toast.success(`Deleted ${b.filename}`);
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const blobs = data?.blobs || [];

  return (
    <div className="space-y-6 max-w-5xl">
      {/* Dropzone */}
      <div
        data-testid="blob-dropzone"
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); upload(e.dataTransfer.files?.[0]); }}
        onClick={() => inputRef.current?.click()}
        className={`border border-dashed rounded-sm p-10 text-center cursor-pointer transition-colors duration-200 ${
          dragOver ? "border-primary bg-primary/5" : "border-border bg-card hover:border-primary/40"
        }`}
      >
        <input ref={inputRef} type="file" data-testid="blob-file-input" className="hidden"
          onChange={(e) => upload(e.target.files?.[0])} />
        {uploading ? (
          <Loader2 className="w-8 h-8 mx-auto text-primary animate-spin" />
        ) : (
          <UploadCloud className="w-8 h-8 mx-auto text-muted-foreground" />
        )}
        <div className="mt-3 text-sm text-white">Drop a file or click to upload</div>
        <div className="font-mono text-[11px] text-muted-foreground mt-1">Stored encrypted at rest in blob storage</div>
      </div>

      {/* List */}
      <div className="border border-border rounded-sm bg-card" data-testid="blob-list">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="flex items-center gap-2">
            <HardDrive className="w-4 h-4 text-primary" />
            <h3 className="text-sm font-medium">Stored Objects</h3>
          </div>
          <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-muted-foreground">{blobs.length}</span>
        </div>
        <div className="divide-y divide-border">
          {blobs.map((b) => (
            <div key={b.id} className="flex items-center justify-between px-6 py-3" data-testid="blob-row">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-9 h-9 rounded-sm border border-border flex items-center justify-center shrink-0">
                  <FileIcon className="w-4 h-4 text-muted-foreground" />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-white truncate">{b.filename}</span>
                    {b.encrypted && (
                      <span data-testid="blob-encrypted-badge"
                        className="inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-[0.08em] px-1.5 py-0.5 rounded-sm border border-emerald-500/30 bg-emerald-500/5 text-emerald-500">
                        <Lock className="w-2.5 h-2.5" /> Encrypted
                      </span>
                    )}
                  </div>
                  <div className="font-mono text-[11px] text-muted-foreground">
                    {humanSize(b.size)} · {b.content_type} · {new Date(b.created_at).toLocaleString()}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0 ml-3">
                <Button variant="ghost" size="icon" data-testid="blob-download" onClick={() => download(b)}
                  className="h-8 w-8 text-muted-foreground hover:text-primary rounded-sm">
                  <Download className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="icon" data-testid="blob-delete" onClick={() => remove(b)}
                  className="h-8 w-8 text-muted-foreground hover:text-destructive rounded-sm">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
          {blobs.length === 0 && (
            <div className="text-center text-sm text-muted-foreground py-10">No objects stored yet.</div>
          )}
        </div>
      </div>
    </div>
  );
}
